<script setup>
/**
 * ModalSimple: Un modal de bootstrap. Este componente solo gestiona los show y hide del modal.
 */
/* global $ */
import {
  provide, ref, useTemplateRef, watch,
} from 'vue';
import ModalBootstrapLayout from './ModalBootstrapLayout.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: true },
  // Si el modal se abre encima de otro, indicar el nivel (2, 3, …). Nivel 1 = bootstrap
  // por defecto, sin clases adicionales. El backdrop asociado recibe la misma clase.
  nivelModal: { type: Number, default: 1 },
});
const emits = defineEmits(['update:modelValue', 'mostrado', 'ocultado']);

const modal = useTemplateRef('modal');
const $modal = ref(null);
const showModal = ref(props.modelValue);
const indiceBackdrop = ref(null);

function claseStack() {
  return `modal-stack-${props.nivelModal}`;
}

function aplicarClaseModalStack() {
  if (props.nivelModal > 1) {
    $modal.value.addClass(claseStack());
  }
}

function backdropDeEsteModal() {
  if (indiceBackdrop.value === null) {
    return $();
  }
  return $('.modal-backdrop').eq(indiceBackdrop.value);
}

function aplicarClaseBackdropStack() {
  if (props.nivelModal <= 1) {
    return;
  }

  const backdrop = backdropDeEsteModal();
  if (!backdrop.length) {
    return;
  }

  backdrop.addClass(claseStack()).attr('data-modal-stack-level', props.nivelModal);
}

function limpiarModalesStacked() {
  if (props.nivelModal > 1) {
    $modal.value.removeClass(claseStack());
    backdropDeEsteModal()
      .removeClass(claseStack())
      .removeAttr('data-modal-stack-level');
    indiceBackdrop.value = null;
  }
}

function controlEventosModalBootstrap() {
  $modal.value.off('show.bs.modal shown.bs.modal hidden.bs.modal');

  $modal.value.on('show.bs.modal', () => {
    aplicarClaseModalStack();
    if (props.nivelModal > 1) {
      setTimeout(() => aplicarClaseBackdropStack(), 0);
    }
  });
  $modal.value.on('shown.bs.modal', (e) => {
    // Foco al primer input
    if (props.autofocus) {
      $(e.currentTarget).find('input:first-child').trigger('focus');
    }
    emits('mostrado');
    aplicarClaseBackdropStack();
  });
  $modal.value.on('hidden.bs.modal', () => {
    limpiarModalesStacked();
    showModal.value = false;
    emits('update:modelValue', false);
    emits('ocultado');
  });
}

function showModalBootstrap() {
  $modal.value = $(modal.value.$el);
  indiceBackdrop.value = $('.modal-backdrop').length;
  controlEventosModalBootstrap();
  $modal.value.modal('show');
}

function hideModalBootstrap() {
  $modal.value?.modal('hide');
}

watch(() => props.modelValue, (value) => {
  showModal.value = value;
  if (!value) {
    hideModalBootstrap();
  } else {
    showModalBootstrap();
  }
});

function mostrarModal() {
  showModal.value = true;
  showModalBootstrap();
}

function cerrarModal() {
  hideModalBootstrap();
}

provide('mostrarModal', mostrarModal);
provide('cerrarModal', cerrarModal);
</script>

<template>
  <Teleport to="body">
    <ModalBootstrapLayout ref="modal" v-bind="$attrs" :show="showModal">
      <slot :mostrar="mostrarModal" :cerrar="cerrarModal"></slot>
      <!--
        Estos templates es para pasar los slots que nos pasan a nosotros
        al componente hijo
      -->
      <template v-for="(index, name) in $slots" #[name]="data">
        <slot :name="name" v-bind="data"></slot>
      </template>
    </ModalBootstrapLayout>
  </Teleport>
</template>
